import * as R from 'ramda';
import Log from '../logging';

export type ErrorTester<TError extends Error> = (error: TError) => boolean;

export default class Result<TError extends Error, TSuccess = boolean> {
  protected error: TError | undefined;

  protected successResult: TSuccess | undefined;

  protected constructor(failure?: TError, success?: TSuccess) {
    this.error = failure;
    this.successResult = success;
  }

  public isSuccessful(): boolean {
    return !R.isNil(this.successResult);
  }

  public success(): TSuccess {
    if (this.successResult === undefined) {
      throw new Error('Cant retrieve the result of a failed result.');
    }

    return this.successResult;
  }

  public isFailed(): boolean {
    return !R.isNil(this.error);
  }

  public failedWith(errorTester: ErrorTester<TError>): boolean {
    if (!this.error) {
      return false;
    }

    return errorTester(this.error);
  }

  public failure(): TError {
    if (!this.error) {
      throw new Error('Cant retrieve the error of a result with no error set.');
    }

    return this.error;
  }

  public failureAs<TSpecificError>(): TSpecificError {
    if (!this.error) {
      throw new Error('Cant treat failure as specific error with no error set.');
    }

    const errorResult: TSpecificError = this.error as unknown as TSpecificError;
    if (!errorResult) {
      throw new Error(`Cannot convert ${this.error} to requested specific error`);
    }

    return errorResult;
  }

  public logError(message = 'Error Result'): void {
    if (!this.error) {
      return;
    }
    Log.error(message, this.error);
  }

  public static successful<TError extends Error, TSuccess>(result: TSuccess): Result<TError, TSuccess> {
    return new Result<TError, TSuccess>(undefined, result);
  }

  public static noValueSuccess<TError extends Error>(): Result<TError> {
    return new Result<TError>(undefined, true);
  }

  public static failed<TError extends Error, TSuccess>(failure: TError): Result<TError, TSuccess> {
    return new Result<TError, TSuccess>(failure);
  }

  public static noValueFailed<TError extends Error>(failure: TError): Result<TError> {
    return new Result<TError>(failure);
  }
}
